import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Cookie, Shield, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });
  const { toast } = useToast();

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem("cookie-consent", JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString(),
    }));
    setIsVisible(false);
    toast({
      title: "Einstellungen gespeichert",
      description: "Ihre Cookie-Präferenzen wurden erfolgreich gespeichert.",
    });
  };

  const acceptAll = () => savePreferences({ necessary: true, analytics: true, marketing: true });
  const acceptNecessary = () => savePreferences({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => savePreferences(preferences);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center md:p-4 pointer-events-none animate-fade-in">
      <div 
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm pointer-events-auto hidden md:block"
        onClick={() => setIsVisible(false)}
      />
      
      <Card className="relative w-full max-w-4xl bg-background border-foreground/10 shadow-2xl pointer-events-auto animate-slide-in-bottom mb-0 md:mb-4 rounded-none md:rounded-lg">
        <div className="p-4 md:p-8">
          <div className="flex items-start justify-between mb-3 md:mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2 md:p-2.5 bg-foreground/5 rounded-lg hidden sm:block">
                <Cookie className="h-5 w-5 text-foreground" />
              </div>
              <div>
                <h3 className="font-caslon text-base md:text-xl font-bold text-foreground">
                  Cookie-Einstellungen
                </h3>
                <p className="text-[11px] md:text-xs text-foreground/70">
                  Wir respektieren Ihre Privatsphäre
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsVisible(false)}
              className="p-1.5 md:p-2 hover:bg-muted rounded-lg transition-colors shrink-0"
              aria-label="Cookie-Hinweis schließen"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <p className="text-xs md:text-sm text-muted-foreground mb-4 md:mb-6 leading-relaxed">
            Wir verwenden Cookies, um Ihre Erfahrung auf unserer Website zu verbessern. 
            <span className="hidden md:inline"> Einige Cookies sind für die Grundfunktionen erforderlich, während andere uns helfen, 
            die Nutzung zu analysieren und Inhalte zu personalisieren.</span>
          </p>

          {showDetails && (
            <div className="mb-6 space-y-3 animate-fade-in">
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <h4 className="font-semibold text-sm text-foreground flex items-center gap-2">
                      <Shield className="h-3.5 w-3.5" />
                      Notwendige Cookies
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Diese Cookies sind für die grundlegende Funktionalität der Website erforderlich.
                    </p>
                  </div>
                  <div className="text-[11px] font-medium text-foreground bg-foreground/5 px-3 py-1 rounded-md">
                    Immer aktiv
                  </div>
                </div>
              </div>

              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm text-foreground">Analyse-Cookies</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Helfen uns zu verstehen, wie Besucher mit der Website interagieren.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-foreground/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-foreground"></div>
                  </label>
                </div>
              </div>

              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm text-foreground">Marketing-Cookies</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Werden verwendet, um Besuchern relevante Werbung anzuzeigen.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-foreground/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-foreground"></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDetails(!showDetails)}
              className="flex-1 rounded-lg text-sm"
            >
              <Settings className="mr-2 h-3.5 w-3.5" />
              {showDetails ? "Details ausblenden" : "Einstellungen anpassen"}
            </Button>
            
            {showDetails ? (
              <>
                <Button variant="outline" onClick={acceptNecessary} className="flex-1 rounded-lg text-sm">
                  Nur notwendige
                </Button>
                <Button onClick={saveCustom} className="flex-1 bg-foreground text-background hover:bg-foreground/90 rounded-lg text-sm">
                  Auswahl speichern
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={acceptNecessary} className="flex-1 rounded-lg text-sm">
                  Nur notwendige
                </Button>
                <Button onClick={acceptAll} className="flex-1 bg-foreground text-background hover:bg-foreground/90 rounded-lg text-sm">
                  Alle akzeptieren
                </Button>
              </>
            )}
          </div>

          <div className="mt-5 pt-4 border-t border-border/50 flex flex-wrap gap-4 text-xs text-foreground/80">
            <a href="/datenschutz" className="hover:text-foreground underline transition-colors">Datenschutzerklärung</a>
            <a href="/impressum" className="hover:text-foreground underline transition-colors">Impressum</a>
            <a href="#" className="hover:text-foreground underline transition-colors">Cookie-Richtlinie</a>
          </div>
        </div>
      </Card>
    </div>
  );
};
